import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DialogModule } from 'primeng/dialog';
import { MessageService } from 'primeng/api';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-whatsapp-communication',
  imports: [CommonModule, ButtonModule, FormsModule, InputTextModule, DialogModule],
  templateUrl: './whatsapp-communication.component.html',
  styleUrl: './whatsapp-communication.component.scss'
})
export class WhatsappCommunicationComponent {
  @Output() closeDialog = new EventEmitter<any>();
  @Input() communicationData: any = {};

  whatsappMessage: string = '';
  editablePhone: string = '';
  editableName: string = '';
  editableEmail: string = '';
  
  isEditingDetails: boolean = false;
  loading: boolean = false;

  constructor(private messageService: MessageService) {}

  ngOnInit() {
    // Initialize editable fields with the lead data
    this.editablePhone = this.communicationData?.phone || '';
    this.editableName = `${this.communicationData?.first_name || ''} ${this.communicationData?.last_name || ''}`.trim();
    this.editableEmail = this.communicationData?.email || '';
    
    // Set a default WhatsApp message
    this.whatsappMessage = `Hi ${this.editableName || 'there'},\n\nI hope this message finds you well. I wanted to reach out regarding your inquiry.\n\nPlease let me know if you have any questions.\n\nBest regards,\nEducation Team`;
  }

  handleCloseDialog() {
    this.closeDialog.emit();
  }

  toggleEditDetails() {
    this.isEditingDetails = !this.isEditingDetails;
  }

  saveEditedDetails() {
    this.isEditingDetails = false;
    // Update the display name in the message if needed
    this.whatsappMessage = this.whatsappMessage.replace(
      new RegExp(`Hi ${this.communicationData?.first_name} ${this.communicationData?.last_name}`, 'g'),
      `Hi ${this.editableName}`
    );
  }

  checkWhatsAppLogin() {
    // Open WhatsApp Web to check login status
    const whatsappWebUrl = 'https://web.whatsapp.com';
    const loginCheckPopup = window.open(
      whatsappWebUrl,
      'whatsapp-login-check',
      'width=1000,height=700,scrollbars=yes,resizable=yes'
    );

    if (loginCheckPopup) {
      this.messageService.add({
        severity: 'info',
        summary: 'WhatsApp Web',
        detail: 'Please login to WhatsApp Web first, then try sending the message again.'
      });
    }
  }

  handleSendWhatsApp() {
    if (!this.editablePhone) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Phone number is required to send WhatsApp message'
      });
      return;
    }

    if (!this.whatsappMessage.trim()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error', 
        detail: 'Please enter a message to send'
      });
      return;
    }

    this.loading = true;

    // Clean phone number (remove spaces, dashes, etc.)
    const cleanPhone = this.editablePhone.replace(/[^\d+]/g, '');
    
    // Encode the message for URL
    const encodedMessage = encodeURIComponent(this.whatsappMessage);
    
    // Try WhatsApp Web first (requires login)
    const whatsappWebUrl = `https://web.whatsapp.com/send?phone=${cleanPhone}&text=${encodedMessage}`;
    
    try {
      // Open WhatsApp Web in a popup window
      const popup = window.open(
        whatsappWebUrl, 
        'whatsapp-popup',
        'width=1000,height=700,scrollbars=yes,resizable=yes,location=yes'
      );
      
      if (popup) {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'WhatsApp Web opened. If you see a QR code, please scan it to login first.'
        });
      } else {
        this.messageService.add({
          severity: 'warn',
          summary: 'Warning',
          detail: 'Popup was blocked. Please allow popups and try again.'
        });
      }
      
      // Close the dialog after a short delay
      setTimeout(() => {
        this.loading = false;
        this.handleCloseDialog();
      }, 2000);
      
    } catch (error) {
      this.loading = false;
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to open WhatsApp Web'
      });
    }
  }

  sendViaWaMe() {
    if (!this.editablePhone || !this.whatsappMessage.trim()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Phone number and message are required'
      });
      return;
    }

    this.loading = true;
    const cleanPhone = this.editablePhone.replace(/[^\d+]/g, '');
    const encodedMessage = encodeURIComponent(this.whatsappMessage);
    const waUrl = `https://wa.me/${cleanPhone}?text=${encodedMessage}`;
    
    try {
      const popup = window.open(
        waUrl, 
        'whatsapp-wa-popup',
        'width=800,height=600,scrollbars=yes,resizable=yes'
      );
      
      if (popup) {
        this.messageService.add({
          severity: 'info',
          summary: 'WhatsApp',
          detail: 'This will open WhatsApp app or ask you to download it. Use "Send via Web" for browser-based messaging.'
        });
      }
      
      setTimeout(() => {
        this.loading = false;
      }, 1000);
      
    } catch (error) {
      this.loading = false;
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to open WhatsApp'
      });
    }
  }

  formatPhoneNumber(phone: string): string {
    // Basic phone number formatting for display
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  }
}
